import { useState, useEffect, useRef, useMemo } from 'react';
import { View, Modal, ScrollView, Pressable, Alert, Platform, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { Calendar } from '@/components/ui/Calendar';
import type { Expense, ExpenseCategory, PaymentMethod, ExpenseSplit } from '@/services/expenses';
import { formatCurrency, type CurrencyCode } from '@/utils/currency';
import { DualCurrencyDisplay } from './DualCurrencyDisplay';
import { ExpenseSplitSelector } from './ExpenseSplitSelector';
import { uploadReceipt } from '@/services/receiptUpload';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Trip } from '@/services/trips';
import { AutocompleteInput } from '@/components/ui/AutocompleteInput';
import { getMerchantSuggestions, getDescriptionSuggestions } from '@/utils/formSuggestions';
import { CURRENCY_OPTIONS } from '@/data/profileOptions';
import { coerceDate } from '@/utils/dateKeys';
import { getTripParticipants } from '@/utils/tripParticipants';
import { networkMonitor } from '@/services/networkMonitor';
import { logger } from '@/services/logger';
import * as Linking from 'expo-linking';

type ExpenseFormProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (expense: Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  tripId: string;
  userId: string;
  initialExpense?: Expense;
  defaultDate?: Date;
  tripStartDate: Date;
  tripEndDate: Date;
  baseCurrency?: CurrencyCode; // Base currency for conversion display
  trip?: Trip; // Trip data for getting members
};

const CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'flight', label: 'Flight' },
  { value: 'lodging', label: 'Lodging' },
  { value: 'food', label: 'Food & Dining' },
  { value: 'transport', label: 'Transportation' },
  { value: 'activity', label: 'Activity' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Credit/Debit Card' },
  { value: 'debit', label: 'Debit Card' },
  { value: 'mobile', label: 'Mobile Payment' },
  { value: 'other', label: 'Other' },
];

const PRE_TRIP_DATE_BUFFER_DAYS = 365; // Allow bookings far in advance (12 months)
const POST_TRIP_DATE_BUFFER_DAYS = 90; // Allow post-trip settlements and refunds (90 days)

const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return normalizeDate(result);
};

const formatDateForMessage = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function ExpenseForm({
  visible,
  onClose,
  onSubmit,
  tripId,
  userId,
  initialExpense,
  defaultDate,
  tripStartDate,
  tripEndDate,
  baseCurrency = 'USD',
  trip,
}: ExpenseFormProps) {
  const theme = useTheme();
  const safeTripStartDate = useMemo(
    () => coerceDate(tripStartDate) ?? new Date(),
    [tripStartDate],
  );
  const safeTripEndDate = useMemo(
    () => coerceDate(tripEndDate) ?? safeTripStartDate,
    [tripEndDate, safeTripStartDate],
  );
  const safeDefaultDate = useMemo(
    () => coerceDate(defaultDate) ?? safeTripStartDate,
    [defaultDate, safeTripStartDate],
  );
  const safeInitialDate = useMemo(
    () => coerceDate(initialExpense?.date) ?? safeDefaultDate,
    [initialExpense?.date, safeDefaultDate],
  );
  const [amount, setAmount] = useState(initialExpense?.amount?.toString() || '');
  const [currency, setCurrency] = useState<string>(initialExpense?.currency || 'USD');
  const [category, setCategory] = useState<ExpenseCategory>(initialExpense?.category || 'other');
  const [description, setDescription] = useState(initialExpense?.description || '');
  const [date, setDate] = useState<Date>(safeInitialDate);
  const [location, setLocation] = useState(initialExpense?.location || '');
  const [merchant, setMerchant] = useState(initialExpense?.merchant || '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(initialExpense?.paymentMethod);
  const [receiptUrl, setReceiptUrl] = useState(initialExpense?.receiptUrl);
  const [submitting, setSubmitting] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [expenseSplit, setExpenseSplit] = useState<ExpenseSplit | null>(initialExpense?.split || null);
  /** Seed for ExpenseSplitSelector — set when form opens, not live expenseSplit (avoids update loops). */
  const [splitInitial, setSplitInitial] = useState<ExpenseSplit | null>(initialExpense?.split || null);
  const [splitMountKey, setSplitMountKey] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [processingExpenseId, setProcessingExpenseId] = useState<string | null>(null);
  const [dateNotice, setDateNotice] = useState<string | null>(null);
  const ocrSubscriptionRef = useRef<(() => void) | null>(null);
  const prevVisibleRef = useRef(false);
  const lastSessionExpenseKeyRef = useRef<string | null>(null);
  const tripParticipants = useMemo(() => getTripParticipants(trip, userId), [trip, userId]);

  const minAllowedDate = useMemo(
    () => addDays(safeTripStartDate, -PRE_TRIP_DATE_BUFFER_DAYS),
    [safeTripStartDate]
  );

  const maxAllowedDate = useMemo(
    () => addDays(safeTripEndDate, POST_TRIP_DATE_BUFFER_DAYS),
    [safeTripEndDate]
  );

  // Reset form when the modal opens or when switching add vs edit / which expense is edited.
  // Do NOT key off trip date object identity (Firestore often delivers new Date/Timestamp refs for the
  // same calendar day), or the form will clear while the user is typing.
  useEffect(() => {
    const cleanupOcr = () => {
      if (ocrSubscriptionRef.current) {
        ocrSubscriptionRef.current();
        ocrSubscriptionRef.current = null;
      }
    };

    if (!visible) {
      prevVisibleRef.current = false;
      lastSessionExpenseKeyRef.current = null;
      cleanupOcr();
      return cleanupOcr;
    }

    const sessionKey = initialExpense?.id ?? '__add__';
    const justOpened = !prevVisibleRef.current;
    prevVisibleRef.current = true;
    const shouldSyncForm = justOpened || lastSessionExpenseKeyRef.current !== sessionKey;

    if (shouldSyncForm) {
      lastSessionExpenseKeyRef.current = sessionKey;
      setSplitMountKey((k) => k + 1);
      if (initialExpense) {
        setAmount(initialExpense.amount.toString());
        setCurrency(initialExpense.currency);
        setCategory(initialExpense.category);
        setDescription(initialExpense.description);
        setDate(coerceDate(initialExpense.date) ?? safeDefaultDate);
        setLocation(initialExpense.location || '');
        setMerchant(initialExpense.merchant || '');
        setPaymentMethod(initialExpense.paymentMethod);
        setReceiptUrl(initialExpense.receiptUrl);
        const splitSeed = initialExpense.split
          ? {
              splitType: initialExpense.split.splitType,
              splitAmong: initialExpense.split.splitAmong,
              paidBy: initialExpense.split.paidBy,
              splitAmounts: initialExpense.split.splitAmounts,
            }
          : null;
        setSplitInitial(splitSeed);
        setExpenseSplit(splitSeed);
      } else {
        setAmount('');
        setCurrency('USD');
        setCategory('other');
        setDescription('');
        setDate(safeDefaultDate);
        setLocation('');
        setMerchant('');
        setPaymentMethod(undefined);
        setReceiptUrl(undefined);
        setSplitInitial(null);
        setExpenseSplit(null);
      }
      setErrors({});
    }

    return cleanupOcr;
    // Omit safeDefaultDate from deps: trip dates often get new Timestamp/Date refs for the same day;
    // we only seed defaults when the modal opens or the edited expense changes (see refs above).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- safeDefaultDate read only when syncing form
  }, [visible, initialExpense?.id, initialExpense]);

  // Provide a soft warning for dates outside the trip window but within the allowed buffer
  useEffect(() => {
    const normalizedDate = normalizeDate(date);
    const normalizedTripStart = normalizeDate(safeTripStartDate);
    const normalizedTripEnd = normalizeDate(safeTripEndDate);

    if (normalizedDate < minAllowedDate || normalizedDate > maxAllowedDate) {
      setDateNotice(null);
      return;
    }

    if (normalizedDate < normalizedTripStart) {
      setDateNotice('This is before your trip—early bookings are allowed.');
    } else if (normalizedDate > normalizedTripEnd) {
      setDateNotice('This is after your trip—post-trip settlements are allowed for 90 days.');
    } else {
      setDateNotice(null);
    }
  }, [date, safeTripStartDate, safeTripEndDate, minAllowedDate, maxAllowedDate]);

  // Subscribe to OCR results when processing
  useEffect(() => {
    if (!processingExpenseId || !tripId) return;

    const expenseRef = doc(db, 'trips', tripId, 'expenses', processingExpenseId);
    
    ocrSubscriptionRef.current = onSnapshot(
      expenseRef,
      (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      
      // Check if OCR is complete
      if (data.status === 'parsed') {
        setProcessingReceipt(false);
        setProcessingExpenseId(null);
        
        // Populate form with OCR results
        // Preserve existing split if user already set one
        const preserveSplit = expenseSplit;
        
        if (data.amount) {
          setAmount(data.amount.toString());
        }
        if (data.currency) {
          setCurrency(data.currency);
        }
        if (data.description || data.merchant) {
          setDescription(data.description || data.merchant || '');
        }
        if (data.merchant) {
          setMerchant(data.merchant);
        }
        if (data.date) {
          const expenseDate = coerceDate(data.date);
          if (expenseDate) {
          setDate(expenseDate);
          }
        }
        if (data.receiptUrl) {
          setReceiptUrl(data.receiptUrl);
        }
        
        // Preserve split information if it was already set
        if (preserveSplit) {
          setExpenseSplit(preserveSplit);
        } else if (data.split) {
          // If OCR somehow extracted split info, use it
          setExpenseSplit(data.split);
        }

        Alert.alert(
          'Receipt Processed',
          'We\'ve extracted the details from your receipt. Please review and make any adjustments.',
          [{ text: 'OK' }]
        );

        // Cleanup subscription
        if (ocrSubscriptionRef.current) {
          ocrSubscriptionRef.current();
          ocrSubscriptionRef.current = null;
        }
      } else if (data.status === 'failed') {
        setProcessingReceipt(false);
        setProcessingExpenseId(null);
        
        Alert.alert(
          'Couldn\'t Read Receipt',
          'We couldn\'t extract details from this receipt. Please enter the information manually.',
          [{ text: 'OK' }]
        );

        // Cleanup subscription
        if (ocrSubscriptionRef.current) {
          ocrSubscriptionRef.current();
          ocrSubscriptionRef.current = null;
        }
      }
      },
      (error) => {
        logger.warn(
          '[ExpenseForm] OCR receipt subscription error',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
        setProcessingReceipt(false);
        setProcessingExpenseId(null);
        if (ocrSubscriptionRef.current) {
          ocrSubscriptionRef.current();
          ocrSubscriptionRef.current = null;
        }
        Alert.alert(
          'Receipt sync interrupted',
          'We lost connection while reading your receipt. Please re-open the form or enter the details manually.',
        );
      },
    );

    // Cleanup on unmount
    return () => {
      if (ocrSubscriptionRef.current) {
        ocrSubscriptionRef.current();
        ocrSubscriptionRef.current = null;
      }
    };
  }, [processingExpenseId, tripId]);

  const convertUriToBlob = async (uri: string, mimeType: string): Promise<Blob> => {
    try {
      const response = await fetch(uri);
      if (!response.ok) {
        throw new Error('Unable to read file');
      }
      return await response.blob();
    } catch (error) {
      console.error('Error converting URI to blob:', error);
      throw error;
    }
  };

  const handleTakePhoto = async () => {
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            'Permission Required',
            'We need camera access to take receipt photos. You can also upload from files or photo library.',
            [
              { text: 'Not now', style: 'cancel' },
              { text: 'Open Settings', onPress: () => void Linking.openSettings() },
            ],
          );
          return;
        }
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        quality: 0.8,
        aspect: [4, 3],
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      let sourceUri = asset.uri;

      const isHeic = asset.mimeType?.toLowerCase().includes('heic') || 
                     asset.mimeType?.toLowerCase().includes('heif');
      
      if (isHeic) {
        const manipulated = await ImageManipulator.manipulateAsync(
          asset.uri,
          [],
          { compress: 0.9, format: ImageManipulator.SaveFormat.JPEG }
        );
        sourceUri = manipulated.uri;
      }

      const blob = await convertUriToBlob(sourceUri, 'image/jpeg');
      await handleReceiptUpload(blob, {
        fileName: asset.fileName || 'receipt.jpg',
        mimeType: 'image/jpeg',
      });
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  const handlePickImage = async () => {
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            'Permission Required',
            'We need photo library access to upload receipts. You can also choose a file instead.',
            [
              { text: 'Use file upload', style: 'cancel' },
              { text: 'Open Settings', onPress: () => void Linking.openSettings() },
            ],
          );
          return;
        }
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: Platform.OS !== 'web',
        quality: 0.8,
        aspect: Platform.OS !== 'web' ? [4, 3] : undefined,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      let sourceUri = asset.uri;

      const isHeic = asset.mimeType?.toLowerCase().includes('heic') || 
                     asset.mimeType?.toLowerCase().includes('heif');
      
      if (isHeic) {
        const manipulated = await ImageManipulator.manipulateAsync(
          asset.uri,
          [],
          { compress: 0.9, format: ImageManipulator.SaveFormat.JPEG }
        );
        sourceUri = manipulated.uri;
      }

      const blob = await convertUriToBlob(sourceUri, asset.mimeType || 'image/jpeg');
      await handleReceiptUpload(blob, {
        fileName: asset.fileName || 'receipt.jpg',
        mimeType: blob.type || 'image/jpeg',
      });
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to access photo library. Please try again.');
    }
  };

  const handlePickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      const blob = await convertUriToBlob(asset.uri, asset.mimeType || 'application/pdf');
      
      await handleReceiptUpload(blob, {
        fileName: asset.name,
        mimeType: asset.mimeType || 'application/pdf',
      });
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document. Please try again.');
    }
  };

  const handleReceiptUpload = async (
    file: Blob,
    options: { fileName?: string; mimeType?: string }
  ) => {
    try {
      setUploadingReceipt(true);

      const expenseId = initialExpense?.id;
      
      const uploadResult = await uploadReceipt(tripId, userId, file, {
        expenseId,
        currency,
        fileName: options.fileName,
        mimeType: options.mimeType,
      });

      if (!uploadResult.success || !uploadResult.expenseId) {
        Alert.alert('Upload Failed', uploadResult.error || 'Could not upload receipt');
        setUploadingReceipt(false);
        return;
      }

      setReceiptUrl(uploadResult.receiptUrl);
      setUploadingReceipt(false);
      setProcessingReceipt(true);
      setProcessingExpenseId(uploadResult.expenseId);

      // Show success message
      Alert.alert(
        '✅ Receipt Uploaded Successfully!',
        'We\'re extracting the details from your receipt. The form will update automatically when ready.',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error uploading receipt:', error);
      Alert.alert('Error', 'Failed to upload receipt. Please try again.');
      setUploadingReceipt(false);
      setProcessingReceipt(false);
    }
  };

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    
    // Validation
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = 'Please enter a valid amount greater than 0';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Validate date is within the allowed window (12 months before start, 90 days after end)
    const normalizedExpenseDate = normalizeDate(date);

    if (normalizedExpenseDate < minAllowedDate || normalizedExpenseDate > maxAllowedDate) {
      newErrors.date = `Expense date must be between ${formatDateForMessage(minAllowedDate)} and ${formatDateForMessage(maxAllowedDate)} (early bookings and late settlements allowed).`;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      // Debug: Log split data before submitting
      if (expenseSplit) {
        console.log('[ExpenseForm] Submitting expense with split:', {
          splitType: expenseSplit.splitType,
          splitAmong: expenseSplit.splitAmong,
          paidBy: expenseSplit.paidBy,
          splitAmounts: expenseSplit.splitAmounts,
          fullSplit: expenseSplit,
        });
      }

      await onSubmit({
        amount: amountNum,
        currency,
        category,
        description: description.trim(),
        date,
        location: location.trim() || undefined,
        merchant: merchant.trim() || undefined,
        paymentMethod: paymentMethod || undefined,
        receiptUrl: receiptUrl || undefined,
        createdBy: userId,
        split: expenseSplit || undefined,
      });
      setErrors({});
      
      // Show success message before closing
      const offlineNote =
        !networkMonitor.isOnline() ? ' It will sync to the cloud when you are back online.' : '';
      Alert.alert(
        initialExpense ? '✅ Expense Updated' : '✅ Expense Added',
        initialExpense
          ? `Your expense has been updated successfully.${offlineNote}`
          : `Your expense has been added successfully.${offlineNote}`,
        [{ text: 'OK', onPress: onClose }]
      );
    } catch (error: any) {
      console.error('Error submitting expense:', error);
      Alert.alert(
        'Error',
        error?.message || 'Failed to save expense. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      accessibilityViewIsModal={true}
      accessibilityLabel={initialExpense ? 'Edit expense' : 'Add expense'}
    >
      <SafeAreaView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        edges={['top', 'bottom', 'left', 'right']}
      >
      <SelectFieldModalProvider>
      <View 
        accessibilityLabel={initialExpense ? 'Edit expense form' : 'Add expense form'}
        style={{ flex: 1 }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(1.5),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderMuted,
          }}
        >
          <ThemedText variant="h1" style={{ fontSize: 20 }}>
            {initialExpense ? 'Edit Expense' : 'Add Expense'}
          </ThemedText>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close form"
            accessibilityHint="Closes the form and discards any unsaved changes"
            onPress={onClose}
            style={({ pressed }) => ({
              padding: theme.spacing(1),
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Ionicons name="close" size={24} color={theme.colors.text} />
          </Pressable>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' ? 'on-drag' : 'none'}
        >
          {/* Error Summary */}
          {Object.keys(errors).length > 0 && (
            <View
              accessibilityRole="alert"
              accessibilityLiveRegion="assertive"
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.error + '15',
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor: theme.colors.error + '40',
                gap: theme.spacing(0.5),
              }}
            >
              <ThemedText variant="bodyStrong" color={theme.colors.error}>
                Please fix the following errors:
              </ThemedText>
              {Object.entries(errors).map(([field, message]) => {
                const fieldLabels: Record<string, string> = {
                  amount: 'Amount',
                  description: 'Description',
                  date: 'Date',
                };
                return (
                  <ThemedText key={field} variant="body" color={theme.colors.error}>
                    • {fieldLabels[field] || field}: {message}
                  </ThemedText>
                );
              })}
            </View>
          )}

          <Card variant="outlined">
            <SubHeading>Amount & Currency</SubHeading>
            <View style={{ flexDirection: 'row', gap: theme.spacing(2), alignItems: 'flex-start' }}>
              <View style={{ flex: 2, minWidth: 0 }}>
                <InputField
                  label="Amount"
                  required
                  value={amount}
                  onChangeText={(text) => {
                    setAmount(text);
                    if (errors.amount) {
                      setErrors(prev => ({ ...prev, amount: '' }));
                    }
                  }}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                  autoCapitalize="none"
                  errorText={errors.amount}
                />
              </View>
              <View style={{ flex: 1, minWidth: 100, flexShrink: 0 }}>
                <SelectField
                  label="Currency"
                  value={currency}
                  options={CURRENCY_OPTIONS}
                  onValueChange={setCurrency}
                />
              </View>
            </View>
            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <View style={{ marginTop: theme.spacing(1), alignItems: 'flex-start' }}>
                <DualCurrencyDisplay
                  amount={parseFloat(amount)}
                  currency={currency}
                  baseCurrency={baseCurrency}
                  showRate={true}
                  size="medium"
                />
              </View>
            )}
          </Card>

          <Card variant="outlined">
            <SubHeading>Details</SubHeading>
            <AutocompleteInput
              label="Description"
              required
              value={description}
              onChangeText={(text) => {
                setDescription(text);
                if (errors.description) {
                  setErrors(prev => ({ ...prev, description: '' }));
                }
              }}
              placeholder="What did you spend on?"
              autoCapitalize="sentences"
              errorText={errors.description}
              suggestions={getDescriptionSuggestions()}
              minQueryLength={1}
              showSuggestionsOnFocus={true}
            />
            <SelectField
              label="Category"
              value={category}
              options={CATEGORY_OPTIONS}
              onValueChange={(value) => setCategory(value as ExpenseCategory)}
            />
            <AutocompleteInput
              label="Merchant / Place"
              value={merchant}
              onChangeText={setMerchant}
              placeholder="e.g., Starbucks, Airport Taxi"
              autoCapitalize="words"
              suggestions={async (query) => await getMerchantSuggestions(userId, tripId, query)}
              minQueryLength={1}
              showSuggestionsOnFocus={true}
            />
            <AutocompleteInput
              label="Location"
              value={location}
              onChangeText={setLocation}
              placeholder="e.g., Tokyo, Japan"
              autoCapitalize="words"
              suggestions={async (query) => {
                const { getLocationSuggestions } = await import('@/utils/formSuggestions');
                return getLocationSuggestions(query);
              }}
              minQueryLength={1}
              showSuggestionsOnFocus={true}
            />
          </Card>

          <Card variant="outlined">
            <SubHeading>Date & Payment</SubHeading>
            <View style={{ gap: theme.spacing(0.5) }}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Expense date, ${date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}. Tap to change date`}
                accessibilityHint="Opens calendar picker to select expense date"
                onPress={() => {
                  setShowDatePicker(true);
                  if (errors.date) {
                    setErrors(prev => ({ ...prev, date: '' }));
                  }
                }}
                style={{
                  paddingVertical: theme.spacing(1.5),
                  paddingHorizontal: theme.spacing(1),
                  borderBottomWidth: 1,
                  borderBottomColor: errors.date ? theme.colors.error : theme.colors.borderMuted,
                  marginBottom: theme.spacing(1),
                  minHeight: 44,
                }}
              >
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <View>
                    <Caption color={errors.date ? theme.colors.error : theme.colors.textSecondary}>Date</Caption>
                    <ThemedText variant="body" style={{ marginTop: theme.spacing(0.25) }}>
                      {date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </ThemedText>
                  </View>
                  <Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
                </View>
              </Pressable>
              {errors.date && (
                <Caption 
                  color={theme.colors.error} 
                  style={{ fontSize: 12, marginTop: -theme.spacing(0.5) }}
                  accessibilityLiveRegion="polite"
                  accessibilityRole="alert"
                >
                  {errors.date}
                </Caption>
              )}
            </View>
            <Calendar
              visible={showDatePicker}
              onClose={() => setShowDatePicker(false)}
              onSelectDate={(selectedDate) => {
                setDate(selectedDate);
                setShowDatePicker(false);
              }}
              selectedDate={date}
              minDate={minAllowedDate}
              maxDate={maxAllowedDate}
              title="Select Expense Date"
            />
            <View style={{ gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                You can log expenses up to 12 months before your trip starts and 90 days after it ends.
              </Caption>
              {dateNotice && (
                <Caption color={theme.colors.primary} style={{ fontSize: 12 }}>
                  {dateNotice}
                </Caption>
              )}
            </View>
            <SelectField
              label="Payment Method"
              value={paymentMethod || ''}
              options={[{ value: '', label: 'Not specified' }, ...PAYMENT_METHOD_OPTIONS]}
              onValueChange={(value) => setPaymentMethod(value ? (value as PaymentMethod) : undefined)}
              placeholder="Select payment method"
            />
          </Card>

          {/* Receipt Upload Section */}
          <Card variant="outlined">
            <SubHeading>Receipt</SubHeading>
            {receiptUrl ? (
              <View style={{ gap: theme.spacing(1) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success || '#34C759'} />
                  <Caption color={theme.colors.textSecondary}>
                    Receipt uploaded
                  </Caption>
                </View>
                {processingReceipt && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Caption>Processing receipt...</Caption>
                  </View>
                )}
              </View>
            ) : (
              <View style={{ gap: theme.spacing(1.5) }}>
                <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
                  Upload a receipt to automatically fill in expense details
                </Caption>
                <View style={{ flexDirection: 'row', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                  {Platform.OS !== 'web' && (
                    <Pressable
                      onPress={handleTakePhoto}
                      disabled={uploadingReceipt || processingReceipt}
                      style={({ pressed }) => ({
                        flex: 1,
                        minWidth: 100,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing(1),
                        paddingVertical: theme.spacing(1.5),
                        paddingHorizontal: theme.spacing(2),
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.backgroundSecondary,
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                        opacity: (uploadingReceipt || processingReceipt || pressed) ? 0.6 : 1,
                      })}
                    >
                      {uploadingReceipt ? (
                        <ActivityIndicator size="small" color={theme.colors.primary} />
                      ) : (
                        <Ionicons name="camera" size={20} color={theme.colors.primary} />
                      )}
                      <ThemedText variant="body" color={theme.colors.primary}>
                        Take Photo
                      </ThemedText>
                    </Pressable>
                  )}
                  <Pressable
                    onPress={handlePickImage}
                    disabled={uploadingReceipt || processingReceipt}
                    style={({ pressed }) => ({
                      flex: 1,
                      minWidth: 100,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing(1),
                      paddingVertical: theme.spacing(1.5),
                      paddingHorizontal: theme.spacing(2),
                      borderRadius: theme.radii.md,
                      backgroundColor: theme.colors.backgroundSecondary,
                      borderWidth: 1,
                      borderColor: theme.colors.border,
                      opacity: (uploadingReceipt || processingReceipt || pressed) ? 0.6 : 1,
                    })}
                  >
                    {uploadingReceipt ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <Ionicons name="image" size={20} color={theme.colors.primary} />
                    )}
                    <ThemedText variant="body" color={theme.colors.primary}>
                      Choose Photo
                    </ThemedText>
                  </Pressable>
                  <Pressable
                    onPress={handlePickDocument}
                    disabled={uploadingReceipt || processingReceipt}
                    style={({ pressed }) => ({
                      flex: 1,
                      minWidth: 100,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing(1),
                      paddingVertical: theme.spacing(1.5),
                      paddingHorizontal: theme.spacing(2),
                      borderRadius: theme.radii.md,
                      backgroundColor: theme.colors.backgroundSecondary,
                      borderWidth: 1,
                      borderColor: theme.colors.border,
                      opacity: (uploadingReceipt || processingReceipt || pressed) ? 0.6 : 1,
                    })}
                  >
                    {uploadingReceipt ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : (
                      <Ionicons name="document" size={20} color={theme.colors.primary} />
                    )}
                    <ThemedText variant="body" color={theme.colors.primary}>
                      Choose File
                    </ThemedText>
                  </Pressable>
                </View>
              </View>
            )}
          </Card>

          {/* Expense Splitting */}
          {tripParticipants.length > 1 && (
            <ExpenseSplitSelector
              key={`split-${splitMountKey}-${initialExpense?.id ?? 'new'}`}
              splitContextId={initialExpense?.id ?? 'new'}
              amount={parseFloat(amount) || 0}
              currency={currency}
              tripMembers={tripParticipants}
              currentUserId={userId}
              initialSplit={splitInitial ?? undefined}
              onSplitChange={setExpenseSplit}
            />
          )}

          <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(2) }}>
            {/* Display validation errors */}
            {Object.keys(errors).length > 0 && (
              <View style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.error + '15',
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor: theme.colors.error + '40',
                gap: theme.spacing(0.5),
              }}>
                <ThemedText variant="bodyStrong" color={theme.colors.error}>
                  Please fix the following errors:
                </ThemedText>
                {Object.entries(errors).map(([field, message]) => (
                  <ThemedText key={field} variant="body" color={theme.colors.error}>
                    • {message}
                  </ThemedText>
                ))}
              </View>
            )}
            <Button
              label={initialExpense ? 'Update Expense' : 'Add Expense'}
              onPress={handleSubmit}
              disabled={submitting}
              accessibilityLabel={submitting 
                ? (initialExpense ? 'Updating expense, please wait' : 'Adding expense, please wait')
                : (initialExpense ? 'Update expense' : 'Add expense')
              }
              accessibilityState={{ disabled: submitting, busy: submitting }}
            />
            <Button
              label="Cancel"
              variant="secondary"
              onPress={onClose}
              disabled={submitting}
              accessibilityLabel="Cancel and close form"
              accessibilityHint="Discards any changes and closes the form"
            />
          </View>
        </ScrollView>
      </View>
      </SelectFieldModalProvider>
      </SafeAreaView>
    </Modal>
  );
}

